import { Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useUnusedSkillsStore } from '../../store/unused-skills.store'
import { UnusedUserSkills } from '../../model/unused-skills-model'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Word } from 'src/feature/localization/component/atom/Word'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { getCardRatingColor } from 'src/feature/my-team/util/skillGapUtils'

const RATING_BOX_VALUES = [1, 2, 3, 4, 5] as const

export type UserUnusedSkillsListProps = {
  userId?: string
}

export const UserUnusedSkillsList = observer(({ userId }: UserUnusedSkillsListProps) => {
  const unusedSkillsStore = useUnusedSkillsStore()
  const { translation } = useLocalizationStore()
  const { isLoading } = unusedSkillsStore.getGenericFieldsState('getUnusedUserSkills')

  const pageTranslation = translation.pages.myUnusedSkills

  useEffect(() => {
    unusedSkillsStore.getUnusedUserSkills(userId)
  }, [unusedSkillsStore, userId])

  const groupedSkills = useMemo(() => {
    return unusedSkillsStore.unusedUserSkills?.reduce((acc, skill) => {
      const category = skill.skill_category

      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(skill)

      return acc
    }, {} as Record<string, UnusedUserSkills[]>) || {}
  }, [unusedSkillsStore.unusedUserSkills])

  const getRatingBoxSx = (rating: number, selectedRating: number) => {
    if (!selectedRating) {
      return {
        ...styles.ratingBox,
        borderColor: ColorTokens.border100,
        backgroundColor: 'background.dark',
        color: 'text.primary',
      }
    }

    const boxColor = getCardRatingColor(selectedRating)
    const isFilled = selectedRating >= rating

    return {
      ...styles.ratingBox,
      borderColor: boxColor,
      backgroundColor: isFilled ? boxColor : 'common.white',
      color: isFilled ? 'common.white' : boxColor,
    }
  }

  return (
    <AppPaper sx={styles.root}>
      <AppBox alignItems="center" gap={5}>
        <Typography variant='h5'>{pageTranslation.title()}</Typography>
        <Word sx={styles.pill}>{unusedSkillsStore.unusedUserSkills?.length || '0'}</Word>
      </AppBox>
      {!isLoading && unusedSkillsStore.unusedUserSkills?.length === 0 && (
        <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>{translation.noData()}</Caption>
      )}
      <AppBox
        flexDirection="column"
        gap={10}
        overflow="scroll"
        width="100%"
        sx={{ padding: '0 10px 0 0' }}
      >
        {!isLoading && unusedSkillsStore.unusedUserSkills?.length > 0 && Object.keys(groupedSkills).map(category => (
          <AppBox key={category} flexDirection="column" gap={10}>
            <Typography sx={{ color: 'text.secondary' }}>{category}</Typography>
            {groupedSkills[category].map(skill => (
              <AppPaper key={skill.id} sx={styles.skillCard}>
                <AppBox sx={styles.skillSummaryRow}>
                  <AppBox sx={styles.skillTextColumn}>
                    <Typography variant='h5'>{skill.skill_name}</Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {`${skill.selected_level || 0}/5`}
                    </Typography>
                  </AppBox>

                  <AppBox sx={styles.ratingBoxes}>
                    {RATING_BOX_VALUES.map(rating => (
                      <AppBox
                        key={`${skill.id}-${rating}`}
                        sx={getRatingBoxSx(rating, skill.selected_level || 0)}
                      >
                        {rating}
                      </AppBox>
                    ))}
                  </AppBox>
                </AppBox>
              </AppPaper>
            ))}
          </AppBox>
        ))}
        {isLoading && (
          <AppSkeleton
            count={5}
            direction="column"
            rects={[
              { width: '20%', height: 15 },
              { width: '90%', height: 15 },
              { width: '90%', height: 15 },
            ]}
          />
        )}
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    padding: '10px 15px',
    borderRadius: '12px',
    gap: 10,
    width: '100%',
    border: 0,
  },
  skillCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '10px 15px',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'transparent',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  skillSummaryRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  skillTextColumn: {
    flexDirection: 'column',
    minWidth: 0,
  },
  ratingBoxes: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    flexWrap: 'nowrap',
  },
  ratingBox: {
    width: 30,
    height: 30,
    borderRadius: '6px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    padding: 0,
    transition: 'transform 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  pill: {
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
  },
}
